import {
  AdminHotel,
  AdminHotelSaveInput,
  HotelRatePlan,
  HotelRatePlanUpsertPayload,
  HotelsListResponse,
  RatePlansListResponse,
} from '../types/HotelAdmin';
import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_TRAVEL_API_BASE_URL as string;

export interface FetchAdminHotelsParams {
  search?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
}

const parseResponseBody = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const toErrorMessage = (body: unknown): string => {
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object') {
    const anyBody = body as any;
    const base = anyBody.message || anyBody.error || '';
    const code = anyBody.code;
    const requestId = anyBody.requestId || anyBody.request_id;
    const suffix = [code ? `code=${code}` : '', requestId ? `requestId=${requestId}` : '']
      .filter(Boolean)
      .join(' | ');
    return base ? `${base}${suffix ? ` (${suffix})` : ''}` : '';
  }
  return '';
};

const ensureSuccess = async (response: Response, fallbackMessage: string) => {
  const body = await parseResponseBody(response);
  if (!response.ok) {
    const details = toErrorMessage(body);
    throw new Error(details || fallbackMessage);
  }
  return body;
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (item === null || item === undefined ? '' : String(item).trim()))
    .filter((item) => item !== '');
};

export const getNormalizedHotelRef = (hotel?: Partial<AdminHotel> | null): string => {
  if (!hotel) return '';
  const rawRef = hotel.hotelRef ?? hotel.hotel_ref ?? hotel.ref ?? hotel.id;
  if (rawRef === null || rawRef === undefined) return '';
  return String(rawRef);
};

const normalizeHotel = (hotel: any): AdminHotel => {
  const hotelRef = getNormalizedHotelRef(hotel);
  return {
    ...hotel,
    hotelRef: hotelRef || undefined,
    amenities: toStringArray(hotel?.amenities),
    images: toStringArray(hotel?.images),
    videos: toStringArray(hotel?.videos),
  };
};

export const getNormalizedRatePlanId = (ratePlan?: Partial<HotelRatePlan> | null): string => {
  if (!ratePlan) return '';
  const rawId = ratePlan.ratePlanId ?? ratePlan.rate_plan_id ?? ratePlan.id;
  if (rawId === null || rawId === undefined) return '';
  return String(rawId);
};

const normalizeRatePlan = (ratePlan: any): HotelRatePlan => {
  const ratePlanId = getNormalizedRatePlanId(ratePlan);
  return {
    ...ratePlan,
    ratePlanId: ratePlanId || undefined,
    pricing_ranges: Array.isArray(ratePlan?.pricing_ranges) ? ratePlan.pricing_ranges : [],
  };
};

const extractItems = (payload: any, listKeys: string[]) => {
  if (Array.isArray(payload)) return payload;

  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.items)) return payload.items;
    if (payload.data && typeof payload.data === 'object') {
      if (Array.isArray(payload.data.items)) return payload.data.items;
      if (Array.isArray(payload.data.data)) return payload.data.data;
      if (Array.isArray(payload.data.results)) return payload.data.results;
      for (const key of listKeys) {
        if (Array.isArray(payload.data[key])) return payload.data[key];
      }
    }
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.results)) return payload.results;

    for (const key of listKeys) {
      if (Array.isArray(payload[key])) return payload[key];
    }
  }

  return [];
};

const normalizeHotelsListResponse = (payload: any): HotelsListResponse => {
  const items = extractItems(payload, ['hotels']).map(normalizeHotel);
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return { ...payload, items };
  }
  return { items };
};

const normalizeRatePlansListResponse = (payload: any): RatePlansListResponse => {
  const items = extractItems(payload, ['ratePlans', 'rate_plans', 'plans']).map(normalizeRatePlan);
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return { ...payload, items };
  }
  return { items };
};

const hasOwn = (obj: Record<string, unknown>, key: string) =>
  Object.prototype.hasOwnProperty.call(obj, key);

const appendPrimitiveIfPresent = (form: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null) return;
  form.append(key, String(value));
};

const buildHotelFormData = (
  payload: Partial<AdminHotelSaveInput>,
  includeDefaults: { amenities: boolean; retainedImages: boolean }
) => {
  const form = new FormData();
  const raw = payload as Record<string, unknown>;

  const primitiveKeys = [
    'name',
    'city_code',
    'city_name',
    'address',
    'description',
    'phone_number',
    'star_rating',
    'checkin_time',
    'checkout_time',
    'display_price',
    'display_currency',
    'latitude',
    'longitude',
    'is_active',
    'source_of_truth',
  ];

  primitiveKeys.forEach((key) => {
    if (hasOwn(raw, key)) {
      appendPrimitiveIfPresent(form, key, raw[key]);
    }
  });

  if (hasOwn(raw, 'amenities') || includeDefaults.amenities) {
    const amenities = Array.isArray(payload.amenities) ? payload.amenities : [];
    form.append('amenities', JSON.stringify(amenities));
  }

  if (hasOwn(raw, 'retainedImages') || includeDefaults.retainedImages) {
    const retainedImages = Array.isArray(payload.retainedImages) ? payload.retainedImages : [];
    form.append('retained_images', JSON.stringify(retainedImages));
  }

  if (hasOwn(raw, 'retainedVideos') || includeDefaults.retainedImages) {
    const retainedVideos = Array.isArray(payload.retainedVideos) ? payload.retainedVideos : [];
    form.append('retained_videos', JSON.stringify(retainedVideos));
  }

  const newImageFiles = Array.isArray(payload.newImageFiles) ? payload.newImageFiles : [];
  newImageFiles.forEach((file) => {
    form.append('images', file);
  });

  const newVideoFiles = Array.isArray(payload.newVideoFiles) ? payload.newVideoFiles : [];
  newVideoFiles.forEach((file) => {
    form.append('videos', file);
  });

  return form;
};

export const fetchAdminHotels = async (params?: FetchAdminHotelsParams): Promise<HotelsListResponse> => {
  const url = new URL(`${API_BASE_URL}/hotels/admin/hotels`);
  const search = params?.search?.trim();
  if (search) url.searchParams.set('search', search);
  if (typeof params?.is_active === 'boolean') {
    url.searchParams.set('is_active', String(params.is_active));
  }
  if (params?.page) url.searchParams.set('page', String(params.page));
  if (params?.limit) url.searchParams.set('limit', String(params.limit));

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getApiHeaders('none'),
  });
  const body = await ensureSuccess(response, 'Failed to fetch hotels');
  return normalizeHotelsListResponse(body);
};

export const createAdminHotel = async (payload: AdminHotelSaveInput): Promise<AdminHotel> => {
  const formData = buildHotelFormData(payload, { amenities: true, retainedImages: true });
  const response = await fetch(`${API_BASE_URL}/hotels/admin/hotels`, {
    method: 'POST',
    headers: getApiHeaders('form-data'),
    body: formData,
  });
  const body = await ensureSuccess(response, 'Failed to create hotel');
  return normalizeHotel(body || {});
};

export const updateAdminHotel = async (
  hotelRef: string,
  payload: Partial<AdminHotelSaveInput>
): Promise<AdminHotel> => {
  const formData = buildHotelFormData(payload, { amenities: false, retainedImages: false });
  const response = await fetch(`${API_BASE_URL}/hotels/admin/hotels/${encodeURIComponent(hotelRef)}`, {
    method: 'PATCH',
    headers: getApiHeaders('form-data'),
    body: formData,
  });
  const body = await ensureSuccess(response, 'Failed to update hotel');
  return normalizeHotel(body || {});
};

export const deleteAdminHotel = async (hotelRef: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/hotels/admin/hotels/${encodeURIComponent(hotelRef)}`, {
    method: 'DELETE',
    headers: getApiHeaders('none'),
  });
  await ensureSuccess(response, 'Failed to delete hotel');
};

export const fetchHotelRatePlans = async (hotelRef: string): Promise<RatePlansListResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/hotels/admin/hotels/${encodeURIComponent(hotelRef)}/rate-plans`,
    {
      method: 'GET',
      headers: getApiHeaders('none'),
    }
  );
  const body = await ensureSuccess(response, 'Failed to fetch rate plans');
  return normalizeRatePlansListResponse(body);
};

export const createHotelRatePlan = async (
  hotelRef: string,
  payload: HotelRatePlanUpsertPayload
): Promise<HotelRatePlan> => {
  const response = await fetch(
    `${API_BASE_URL}/hotels/admin/hotels/${encodeURIComponent(hotelRef)}/rate-plans`,
    {
      method: 'POST',
      headers: getApiHeaders('json'),
      body: JSON.stringify(payload),
    }
  );
  const body = await ensureSuccess(response, 'Failed to create rate plan');
  return normalizeRatePlan(body || {});
};

export const updateHotelRatePlan = async (
  ratePlanId: string,
  payload: Partial<HotelRatePlanUpsertPayload>
): Promise<HotelRatePlan> => {
  const response = await fetch(
    `${API_BASE_URL}/hotels/admin/rate-plans/${encodeURIComponent(ratePlanId)}`,
    {
      method: 'PATCH',
      headers: getApiHeaders('json'),
      body: JSON.stringify(payload),
    }
  );
  const body = await ensureSuccess(response, 'Failed to update rate plan');
  return normalizeRatePlan(body || {});
};

export const deleteHotelRatePlan = async (ratePlanId: string): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/hotels/admin/rate-plans/${encodeURIComponent(ratePlanId)}`,
    {
      method: 'DELETE',
      headers: getApiHeaders('none'),
    }
  );
  await ensureSuccess(response, 'Failed to delete rate plan');
};
